import { useState } from "react";
import { useAppTheme } from "../../useAppTheme";
import { DataCardMain } from "./DataCardContent";
import { EmptyState, ErrorState, LoadingState } from "./StateRenderers";
import { CARD_DIMENSIONS } from "./constants";
import type { ChartType, DataCardProps } from "./types";

const shouldShowExtendedDetails = (
	config: { showExtendedDetails?: boolean; expandable?: boolean },
	hasData: boolean,
	expanded: boolean,
) =>
	config.showExtendedDetails &&
	hasData &&
	(config.expandable ? expanded : true);

const getCardStyles = (
	_expanded: boolean,
	themeColors: { primaryContainer: string; surface: string },
) => ({
	width: "100%",
	maxWidth: CARD_DIMENSIONS.maxWidth,
	borderRadius: CARD_DIMENSIONS.borderRadius,
	borderWidth: 1,
	borderColor: themeColors.primaryContainer,
	backgroundColor: themeColors.surface,
	overflow: "hidden" as const,
});

export const DataCard = <T extends ChartType = ChartType>(
	props: DataCardProps<T>,
) => {
	const {
		data,
		config,
		handlers,
		customization,
		state,
		theme: appTheme,
	} = props;
	const theme = useAppTheme();

	const [expanded, setExpanded] = useState(config.isExpanded ?? false);
	const [showInsightDetails, setShowInsightDetails] = useState(
		config.showInsightDetails ?? false,
	);

	const handleExpandToggle = () => {
		setExpanded(!expanded);
		handlers?.onExpandToggle?.();
	};

	if (state?.isLoading) {
		return <LoadingState customLoader={customization?.loader} />;
	}

	if (state?.error) {
		return <ErrorState customErrorComponent={customization?.errorComponent} />;
	}

	const hasNoData =
		!data.chartData ||
		(Array.isArray(data.chartData) && data.chartData.length === 0);

	if (hasNoData) {
		return (
			<EmptyState
				customEmptyStateComponent={customization?.emptyStateComponent}
			/>
		);
	}

	const cardStyles = getCardStyles(expanded, theme.colors);
	const showExtDetails =
		shouldShowExtendedDetails(
			config,
			Boolean(data.extendedDetailsData),
			expanded,
		) ?? false;
	const hasInsightDetails = Boolean(customization?.insightDetails);
	const handleInsightPress = () => {
		if (config.expandable) {
			handleExpandToggle();
		} else if (hasInsightDetails) {
			setShowInsightDetails((prev) => !prev);
		}
		handlers?.onInsightPress?.();
	};
	const onInsightPress =
		config.expandable || hasInsightDetails || handlers?.onInsightPress
			? handleInsightPress
			: undefined;

	return (
		<DataCardMain
			data={data}
			config={config}
			handlers={handlers}
			customization={customization}
			theme={appTheme}
			cardStyles={cardStyles}
			showExtDetails={showExtDetails}
			onInsightPress={onInsightPress}
			insightDetails={customization?.insightDetails}
			showInsightDetails={showInsightDetails}
		/>
	);
};

export type {
	ChartType,
	ChartTypeDataMap,
	DataCardProps,
	DataCardData,
	DataCardConfig,
	DataCardHandlers,
	DataCardCustomization,
} from "./types";
export type {
	ComparisonChartData,
	LineChartData,
	BarChartData,
	RadarChartData,
	CircularChartData,
	CircularProgressListData,
	CircularGaugeGridData,
} from "./types";
